import {
  getEventIds,
  getEventOthersFilter,
  getEventsCsv,
  getPeriodsState,
  getUniqueChar,
} from '../reducers/analyticsApp';
import { AGGREGATIONS_CSV, PARAM_CSV } from '../utils/csv';
import { createOthersQuery, createQuery, getTypeField } from '../utils/event/createQueries';
import { toEventResult } from './eventConverter';
import { toCsvEvents, toCsvEventsWithAdditional, toCsvEventsWithOthers } from './csv/csvConverter';
import { getPeriodUniqueChar } from '../actions/common';

const addAggregation = {
  count: (_, aggregations) =>
    aggregations.some(({ type }) =>
      [
        AGGREGATIONS_CSV.COUNT,
        AGGREGATIONS_CSV.USERS,
        AGGREGATIONS_CSV.AVERAGE_COUNT,
        AGGREGATIONS_CSV.AVERAGE_COUNT_SPLIT,
      ].includes(type)
    )
      ? []
      : [
          {
            type: AGGREGATIONS_CSV.COUNT,
            field: null,
          },
        ],
  max: (field) =>
    field
      ? [
          {
            type: AGGREGATIONS_CSV.MAX,
            field,
          },
        ]
      : [],
  min: (field) =>
    field
      ? [
          {
            type: AGGREGATIONS_CSV.MIN,
            field,
          },
        ]
      : [],
  // avg может быть получен только если уже выбрана агрегация TOTAL, иначе avg получена не будет.
  avg: (field, aggregations) =>
    aggregations.every(({ type }) => type === AGGREGATIONS_CSV.TOTAL)
      ? [
          {
            type: AGGREGATIONS_CSV.COUNT,
            field: null,
          },
        ]
      : [],
};

// когда запрос происходит для файла csv, то появляется необходимость в дополнительных агрегациях. Здесь они и
// добавляются в зависимости от csvState
const getCsvAggregations = (aggregations, csvState, aggregationField) =>
  Object.entries(csvState)
    .map(([key, val]) => (val ? addAggregation[key] : undefined))
    .filter((val) => !!val)
    .flatMap((fn) => fn(aggregationField, aggregations))
    // получаем уникальные значения, тк имеется вероятность того, что агрегации полученные согласно csvState
    // пересекаются с агрегациями составленного запроса.
    .filter((val, index, arr) => arr.map(({ type }) => type).indexOf(val.type) === index)
    .concat(aggregations);

const createCSVQueries = (state) => {
  const ids = getEventIds(state);
  const isDownloadPeriods = getEventsCsv(state)[PARAM_CSV.PERIODS];

  const getPeriodsQueries = (id) =>
    getPeriodsState(state, id)
      .periods.filter((period) => !!period.from.length)
      .map((period) => createQuery(state, id, period));

  const getSplitWithOtherQueries = (id) => {
    const others = getEventOthersFilter(state, id);

    const splitQuery = createQuery(state, id);
    const othersQuery = createOthersQuery(state, id, others);
    return [splitQuery, othersQuery];
  };

  const getQueries = (id) => {
    const others = getEventOthersFilter(state, id);
    const query = createQuery(state, id);
    const periodQueries = getPeriodsQueries(id);

    if (!others) {
      return isDownloadPeriods ? [query].concat(periodQueries) : [query];
    }
    return getSplitWithOtherQueries(id);
  };

  return ids.flatMap((id) => getQueries(id));
};

export const buildCsvQueries = (state) => {
  const csvState = getEventsCsv(state);

  const buildCsvQuery = (query) => {
    const aggregation = query.aggregations.find(({ field }) => field) || {};
    const aggregationField = aggregation.field;

    const aggregations = getCsvAggregations(query.aggregations, csvState, aggregationField);
    return {
      ...query,
      aggregations,
    };
  };

  const CSVQueries = createCSVQueries(state);

  return CSVQueries.map(buildCsvQuery);
};

const getCsvFileName = (eventName, uniqueChar) => `${eventName}-${uniqueChar}.csv`;

const createCSVCharArray = (state) => {
  const ids = getEventIds(state);
  const isDownloadPeriods = getEventsCsv(state)[PARAM_CSV.PERIODS];
  const getSplitWithOthersChar = (id) =>
    !getEventOthersFilter(state, id) ? [] : `${getUniqueChar(state, id)}.Others`;

  return ids.flatMap((id) => {
    // если чекбокс periods отключен, игнорируем доп.периоды
    const periodUniqueChar = isDownloadPeriods
      ? getPeriodUniqueChar(id, getPeriodsState(state, id).periods).map(
          (c) => `${getUniqueChar(state, id)}${c}`
        )
      : [];

    return [getUniqueChar(state, id)].concat(periodUniqueChar).concat(getSplitWithOthersChar(id));
  });
};

const addCachedLabel = (result) => ({
  ...result,
  info: {
    ...result.info,
    eventName: `${result.info.eventName} [cached]`,
  },
});

export const getEventsCsvInfo = (state) => {
  const charsArray = createCSVCharArray(state);
  const getType = (field) => getTypeField(field, state);
  const eventsCsv = getEventsCsv(state);

  return { charsArray, getType, eventsCsv };
};

export const fromEventCsvResponse = (response, queries, eventsCsvInfo) => {
  const { charsArray, getType, eventsCsv } = eventsCsvInfo;

  const isEventsWithOthers =
    charsArray.length > 1 && charsArray.some((char) => char.includes('Others'));
  const eventsResult = response
    .map((resultItem, index) =>
      toEventResult(resultItem, queries[index], charsArray[index], getType, isEventsWithOthers)
    )
    .map((resultItem, index) =>
      !response[index].cached ? resultItem : addCachedLabel(resultItem)
    );

  // комбинируем ивенты с дополнительным периодом или compare with others
  const preparedResult = eventsResult.reduce((combinedEvents, event) => {
    const eventIndexWithSameUniqueChar = combinedEvents.findIndex((eventsData) =>
      eventsData.some((eventVar) => event.info.uniqueChar[0] === eventVar.info.uniqueChar[0])
    );
    if (eventIndexWithSameUniqueChar !== -1) {
      combinedEvents[eventIndexWithSameUniqueChar].push(event);
      return combinedEvents;
    }
    combinedEvents.push([event]);
    return combinedEvents;
  }, []);

  const eventToCsv = (resultItems) => {
    const isAdditionalPeriods =
      resultItems.length > 1 &&
      resultItems.some(
        ({ info }) => info.uniqueChar.length > 1 && !info.uniqueChar.includes('Others')
      );

    if (resultItems.length > 1) {
      return isAdditionalPeriods
        ? toCsvEventsWithAdditional(resultItems, getType, eventsCsv)
        : toCsvEventsWithOthers(resultItems, getType, eventsCsv);
    }

    return toCsvEvents(resultItems[0], getType, eventsCsv);
  };

  return preparedResult.map((events) => ({
    name: getCsvFileName(events[0].info.query.eventName, events[0].info.uniqueChar),
    csv: eventToCsv(events),
  }));
};
